/**
 * ShakespeareAI First-Party Tracker
 * 
 * Identity model:
 *   visitor_id  — persistent UUID in localStorage, survives sessions forever
 *   session_id  — UUID in sessionStorage, new per browser tab, 30min timeout
 *   user_id     — set after login/register, sent with all subsequent events
 *
 * UTM model:
 *   first_touch — captured on FIRST visit with UTMs, never overwritten (localStorage)
 *   last_touch  — updated every time new UTMs appear in URL (localStorage)
 *   Both are sent with every event
 *
 * Events are fire-and-forget POST to /api/track
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.shakespeareai.net/api';

// ---- Identity ----

const VISITOR_KEY = 'sa_visitor_id';
const SESSION_KEY = 'sa_session';
const SESSION_DURATION = 30 * 60 * 1000;

function getVisitorId(): string {
  let id = localStorage.getItem(VISITOR_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(VISITOR_KEY, id);
  }
  return id;
}

function getSessionId(): string {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (raw) {
      const { id, ts } = JSON.parse(raw);
      if (Date.now() - ts < SESSION_DURATION) {
        // Refresh timestamp
        sessionStorage.setItem(SESSION_KEY, JSON.stringify({ id, ts: Date.now() }));
        return id;
      }
    }
  } catch {}
  const id = crypto.randomUUID();
  sessionStorage.setItem(SESSION_KEY, JSON.stringify({ id, ts: Date.now() }));
  return id;
}

// ---- UTM Capture ----

const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid'];
const FIRST_TOUCH_KEY = 'sa_first_touch';
const LAST_TOUCH_KEY = 'sa_last_touch';

function captureUTMs(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const utms: Record<string, string> = {};
    let hasUTM = false;
    
    for (const key of UTM_PARAMS) {
      const val = params.get(key);
      if (val) { utms[key] = val; hasUTM = true; }
    }
    
    // Capture referrer (external only)
    if (document.referrer && !document.referrer.includes(window.location.hostname)) {
      utms.referrer = document.referrer;
      hasUTM = true;
    }
    
    if (!hasUTM) return;
    
    // First touch — set once, never overwrite
    if (!localStorage.getItem(FIRST_TOUCH_KEY)) {
      localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(utms));
    }
    
    // Last touch — always update
    localStorage.setItem(LAST_TOUCH_KEY, JSON.stringify(utms));
  } catch {}
}

export function getFirstTouch(): Record<string, string> {
  try { return JSON.parse(localStorage.getItem(FIRST_TOUCH_KEY) || '{}'); } catch { return {}; }
}

function getLastTouch(): Record<string, string> {
  try { return JSON.parse(localStorage.getItem(LAST_TOUCH_KEY) || '{}'); } catch { return {}; }
}

// Capture on module load
captureUTMs();

// ---- Core Track Function ----

let _userId: number | null = null;

export function setUserId(id: number | null): void {
  _userId = id;
}

export function getVisitor(): string {
  try { return getVisitorId(); } catch { return ''; }
}

export function track(event: string, properties: Record<string, any> = {}): void {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    
    const payload = {
      event,
      visitor_id: getVisitorId(),
      session_id: getSessionId(),
      user_id: _userId,
      first_touch: getFirstTouch(),
      last_touch: getLastTouch(),
      properties: {
        page_path: window.location.pathname,
        page_url: window.location.origin + window.location.pathname, // Never collect OAuth tokens or manuscript/query text.
        page_title: document.title,
        screen_width: window.innerWidth,
        ...properties
      },
      timestamp: new Date().toISOString()
    };
    
    // Use sendBeacon for page_view (survives navigation), fetch for others
    if (event === 'page_view' && navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
      navigator.sendBeacon(`${API_BASE_URL}/track`, blob);
    } else {
      fetch(`${API_BASE_URL}/track`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
        keepalive: true
      }).catch(() => {});
    }
  } catch {}
}

// ---- Convenience Methods ----

export function trackPageView(): void {
  track('page_view');
}

export function trackSignup(): void {
  track('signup');
}

export function trackLogin(): void {
  track('login');
}

export function trackBookStart(genre: string, pages: number): void {
  track('book_start', { genre, pages });
}

export function trackBookComplete(genre: string, pages: number, durationMs: number): void {
  track('book_complete', { genre, pages, duration_ms: durationMs });
}

export function trackDownload(format: string): void {
  track('download', { format });
}

export function trackUpgrade(planName: string, price: number): void {
  track('upgrade', { plan_name: planName, price });
}

export default { track, trackPageView, trackSignup, trackLogin, trackBookStart, trackBookComplete, trackDownload, trackUpgrade, setUserId, getVisitor };
